import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  input,
  OnInit,
  output,
  Renderer2,
  signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fromEvent, Observable } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { InternalLinkClickDirective } from '@amfam/shared/analytics/lib/components/internal-link-click/internal-link-click.directive';
import { InternalLinkClickParams } from '@amfam/shared/analytics/lib/models/analytics.model';
import type { LogoModel } from '@amfam/shared/models/lib/brand/logo.model';
import { BrandLinkDataModel } from '@amfam/shared/utility/brand/lib/models/brand.model';
import { ContactDetails } from '@amfam/shared/utility/contact-us/lib/models/contact-us.model';

import { LinkClickEvent, NavLinkTreeObj, SublinkClickEvent } from '../../models/links-model';
import { NotificationObj } from '../../models/notification-model';
import { HeaderWrapperService } from '../../services/header-wrapper.service';
import { DsHeaderMobileNavigationComponent } from '../ds-header-mobile-navigation/ds-header-mobile-navigation.component';
import { DsHeaderNavigationComponent } from '../ds-header-navigation/ds-header-navigation.component';

@Component({
  selector: 'ds-header',
  templateUrl: './ds-header.component.html',
  styleUrls: ['./ds-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DsHeaderNavigationComponent,
    DsHeaderMobileNavigationComponent,
    InternalLinkClickDirective
  ]
})
export class DsHeaderComponent implements OnInit, AfterViewInit {
  private elemRef = inject(ElementRef);
  private headerWrapperService = inject(HeaderWrapperService);
  private renderer = inject(Renderer2);
  private destroyRef = inject(DestroyRef);

  // Values, not Observables: null is what an upstream `| async` yields before its first value.
  readonly headerLogo = input.required<LogoModel | null>();
  readonly brandInfo = input.required<BrandLinkDataModel | null>();
  readonly homeUrl = input.required<string>();
  readonly loggedIn = input.required<boolean | null>();
  readonly isPartner = input.required<boolean | null>();
  // An event stream, not template state: each emission re-runs the nav items' active-link check.
  readonly loaded$ = input<Observable<boolean>>(this.headerWrapperService.loaded$);
  readonly skipContentFocus = input.required<boolean>();
  readonly notifications = input.required<NotificationObj[] | null>();
  readonly linkArray = input.required<NavLinkTreeObj[] | null>();
  readonly contactDetails = input<ContactDetails | null>();

  readonly linkClickEvent = output<LinkClickEvent>();
  readonly sublinkClickEvent = output<SublinkClickEvent>();
  readonly notificationDismissClickEvent = output<NotificationObj>();
  // TODO: jjc - get this '960' from the design token breakpoints
  private mobileMenuBreakpoint = 960;
  readonly internalLinkClickObject = signal<InternalLinkClickParams | undefined>(undefined);

  ngOnInit() {
    // If mobile menu is open, lock scroll on the body element
    this.headerWrapperService.mobileMenuOpen$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(mobileMenuOpen =>
        mobileMenuOpen
          ? this.renderer.setStyle(document.body, 'overflow', 'hidden')
          : this.renderer.removeStyle(document.body, 'overflow')
      );
  }

  ngAfterViewInit() {
    this.isMobileMenuActive();
    const resizeEvent = fromEvent(window, 'resize').pipe(debounceTime(200));
    resizeEvent.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(this.handleResize.bind(this));
    this.headerWrapperService.setHeaderNavRect(this.elemRef.nativeElement.getBoundingClientRect());
  }

  handleResize() {
    this.isMobileMenuActive();
    this.headerWrapperService.setHeaderNavRect(this.elemRef.nativeElement.getBoundingClientRect());
  }

  isMobileMenuActive() {
    this.headerWrapperService.setMobileMenuActive(
      document.documentElement.clientWidth < this.mobileMenuBreakpoint
    );
  }

  linkClicked(event: LinkClickEvent) {
    if (event) {
      this.linkClickEvent.emit(event);
      if (event.link?.linkRoute === '/claimsV2') {
        this.internalLinkClickObject.set({
          click_id: 'MyAccount_claims_menu_bar',
          click_text: 'top navigation:claims'
        });
      }
    }
  }

  sublinkClicked(event: SublinkClickEvent) {
    if (event) {
      this.sublinkClickEvent.emit(event);
      if (event.sublink?.linkRoute === '/claimsV2') {
        this.internalLinkClickObject.set({
          click_id: 'MyAccount_claims_overview_menu_bar',
          click_text: 'top navigation:claims overview'
        });
      }
      if (event.sublink?.linkRoute === '/claimsV2/risk-selection') {
        this.internalLinkClickObject.set({
          click_id: 'MyAccount_file_a_claim_menu_bar',
          click_text: 'top navigation:file a claim'
        });
      }
      if (event.sublink?.linkRoute === '/faq/claims') {
        this.internalLinkClickObject.set({
          click_id: 'MyAccount_claims_faq_menu_bar',
          click_text: 'top navigation:claims FAQ'
        });
      }
    }
  }
}
